/** Phase 235 — Live status-bar theme-color updates.
 * Reads the *resolved* theme (light/dark) and updates the meta `theme-color`
 * tag so installed PWAs match the in-app theme on the OS chrome
 * (Android pull-down bar, iOS Safari URL bar / standalone mode).
 *
 * Without this hook, the page is shipped with @media-based static
 * theme-color tags that only react to OS preference, not the user's
 * in-app toggle. */

import { useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";

const COLOURS = {
  light: "#ffffff",
  dark: "#09243C",
};

export function useStatusBarTheme() {
  const { theme } = useTheme();

  useEffect(() => {
    const resolved = theme === "system"
      ? (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
      : theme;
    const colour = COLOURS[resolved] || COLOURS.light;

    // Strip any media-based tags so a single resolved value wins
    document.head
      .querySelectorAll('meta[name="theme-color"][media]')
      .forEach((el) => el.parentNode.removeChild(el));

    let tag = document.head.querySelector('meta[name="theme-color"]:not([media])');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "theme-color");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", colour);

    // iOS standalone status-bar style — black-translucent works for both
    // themes since our content has its own background colour
    let appleTag = document.head.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (!appleTag) {
      appleTag = document.createElement("meta");
      appleTag.setAttribute("name", "apple-mobile-web-app-status-bar-style");
      document.head.appendChild(appleTag);
    }
    appleTag.setAttribute("content", resolved === "dark" ? "black-translucent" : "default");
  }, [theme]);
}
